import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { eventsAPI } from '../../utils/api';

// LiveEventsPage — recent telemetry events tail with filter chips for the
// new v1.11.20 event types. Polls every 5s. Drives the immediate visibility
// requirement from docs/zebcast_telemetry.md §A.15 ("see events as they
// occur"). The full SSE-based implementation is planned for web phase w2;
// this is a minimal poll-based version that ships with v1.11.20-a.

const V11120_EVENT_TYPES = [
    'app_session_started',
    'app_session_terminated',
    'session_terminated',
    'protocol_error',
    'session_quality_sample',
];

export default function LiveEventsPage() {
    const [events, setEvents] = useState([]);
    const [typeSummary, setTypeSummary] = useState([]);
    const [integrity, setIntegrity] = useState(null);
    const [deathKinds, setDeathKinds] = useState([]);
    const [castOutcomes, setCastOutcomes] = useState([]);
    const [filter, setFilter] = useState('');
    const [paused, setPaused] = useState(false);
    const [loading, setLoading] = useState(true);
    const [selectedEvent, setSelectedEvent] = useState(null);
    // sessionFilter: when set, only events tagged with this session_id show
    // up in the Recent Events table. Clicking a session_id chip in any row
    // sets it; the X button on the active-filter banner clears it.
    const [sessionFilter, setSessionFilter] = useState('');
    const intervalRef = useRef(null);

    const fetchAll = async () => {
        try {
            const [recentRes, typesRes, intRes, dkRes, coRes] = await Promise.all([
                eventsAPI.getRecent(200, filter),
                eventsAPI.getEventTypeSummary(),
                eventsAPI.getIntegrityStats(),
                eventsAPI.getDeathKinds(),
                eventsAPI.getCastOutcomes(),
            ]);
            setEvents(recentRes.data.events || []);
            setTypeSummary(typesRes.data.types || []);
            setIntegrity(intRes.data);
            setDeathKinds(dkRes.data.rows || []);
            setCastOutcomes(coRes.data.rows || []);
        } catch (err) {
            console.error('LiveEventsPage fetch failed:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAll();
        if (!paused) {
            intervalRef.current = setInterval(fetchAll, 5000);
        }
        return () => intervalRef.current && clearInterval(intervalRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter, paused]);

    const isV11120 = (t) => V11120_EVENT_TYPES.includes(t);

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Live Events</h2>
                <div className="flex gap-2">
                    <button
                        onClick={() => setPaused(p => !p)}
                        className={`px-3 py-1 rounded text-sm font-medium ${
                            paused ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                        }`}>
                        {paused ? '▶ Resume' : '⏸ Pause'}
                    </button>
                    <button
                        onClick={fetchAll}
                        className="px-3 py-1 rounded text-sm font-medium bg-blue-100 text-blue-800">
                        ↺ Refresh
                    </button>
                </div>
            </div>

            {/* Top KPI strip */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <KpiCard
                    title="Integrity Mode"
                    value={integrity?.mode || '—'}
                    subtitle={`${integrity?.total || 0} req · ${integrity?.with_token || 0} w/ token`}
                />
                <KpiCard
                    title="Events (last 24h)"
                    value={typeSummary.reduce((sum, r) => sum + r.count, 0)}
                    subtitle={`${typeSummary.length} distinct types`}
                />
                <KpiCard
                    title="v1.11.20 Events"
                    value={typeSummary.filter(r => isV11120(r.event_type)).reduce((s, r) => s + r.count, 0)}
                    subtitle={V11120_EVENT_TYPES.filter(t => typeSummary.some(r => r.event_type === t)).join(', ') || 'none yet'}
                />
                <KpiCard
                    title="Top Event Type"
                    value={typeSummary[0]?.event_type || '—'}
                    subtitle={`${typeSummary[0]?.count || 0} events`}
                />
            </div>

            {/* Filter chips */}
            <div className="bg-white rounded-lg shadow-md p-4 mb-6">
                <div className="text-xs text-gray-500 mb-2 uppercase tracking-wider">Filter by event type</div>
                <div className="flex flex-wrap gap-2">
                    <Chip active={filter === ''} onClick={() => setFilter('')}>All</Chip>
                    {V11120_EVENT_TYPES.map(t => (
                        <Chip
                            key={t}
                            active={filter === t}
                            onClick={() => setFilter(t === filter ? '' : t)}
                            badge={typeSummary.find(r => r.event_type === t)?.count || 0}
                            highlight>
                            {t}
                        </Chip>
                    ))}
                    {typeSummary.filter(r => !isV11120(r.event_type)).slice(0, 8).map(r => (
                        <Chip
                            key={r.event_type}
                            active={filter === r.event_type}
                            onClick={() => setFilter(r.event_type === filter ? '' : r.event_type)}
                            badge={r.count}>
                            {r.event_type}
                        </Chip>
                    ))}
                </div>
            </div>

            {/* Active session-filter banner */}
            {sessionFilter && (
                <div className="bg-purple-100 border border-purple-300 rounded-lg p-3 mb-4 flex items-center gap-3">
                    <span className="text-sm text-purple-900">
                        Filtering to cast session{' '}
                        <code className="font-mono bg-white px-1.5 py-0.5 rounded">{sessionFilter}</code>
                        {' '}— showing every event correlated to one connection attempt.
                    </span>
                    <button
                        onClick={() => setSessionFilter('')}
                        className="ml-auto text-purple-900 hover:text-purple-700 text-sm font-medium">
                        × clear
                    </button>
                </div>
            )}

            {/* Event tail */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    Recent Events {filter && <span className="text-sm font-normal text-gray-500">(type: {filter})</span>}
                </h3>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Event</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Layer</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Cast session</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">App session</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Detail</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {(sessionFilter
                                ? events.filter(ev => ev.properties?.session_id === sessionFilter)
                                : events
                            ).map(ev => (
                                <tr
                                    key={ev.id}
                                    onClick={() => setSelectedEvent(ev)}
                                    className={`cursor-pointer hover:bg-yellow-50 ${
                                        isV11120(ev.event_type) ? 'bg-blue-50' : ''
                                    } ${selectedEvent?.id === ev.id ? 'ring-2 ring-blue-400 bg-blue-100' : ''}`}>
                                    <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500">
                                        {new Date(ev.timestamp).toLocaleTimeString()}
                                    </td>
                                    <td className="px-3 py-2 whitespace-nowrap text-xs">
                                        <span className={`px-2 py-0.5 rounded font-mono text-xs ${
                                            isV11120(ev.event_type) ? 'bg-blue-100 text-blue-900' : 'bg-gray-100 text-gray-800'
                                        }`}>
                                            {ev.event_type}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-700">
                                        {ev.properties?.telemetry_layer || '—'}
                                    </td>
                                    <td className="px-3 py-2 whitespace-nowrap text-xs font-mono">
                                        {ev.properties?.session_id ? (
                                            <span
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSessionFilter(ev.properties.session_id);
                                                }}
                                                className="px-1.5 py-0.5 rounded bg-purple-100 text-purple-800 hover:bg-purple-200 cursor-pointer"
                                                title={`Filter to session ${ev.properties.session_id}`}>
                                                {ev.properties.session_id.substring(0, 8)}
                                            </span>
                                        ) : <span className="text-gray-400">—</span>}
                                    </td>
                                    <td className="px-3 py-2 whitespace-nowrap text-xs font-mono">
                                        {ev.properties?.app_session_id ? (
                                            <span className="text-gray-500">{ev.properties.app_session_id.substring(0, 8)}</span>
                                        ) : <span className="text-gray-400">—</span>}
                                    </td>
                                    <td className="px-3 py-2 text-xs text-gray-600 max-w-md truncate">
                                        {summarise(ev)}
                                    </td>
                                </tr>
                            ))}
                            {events.length === 0 && (
                                <tr><td colSpan="6" className="px-3 py-6 text-center text-sm text-gray-400">{loading ? 'Loading…' : 'No events match filter'}</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Death-kind leaderboard (v1.11.20 §C.3) */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">App Death Kinds (last 30d)</h3>
                {deathKinds.length === 0 ? (
                    <p className="text-sm text-gray-400">No app_session_terminated events yet — devices on v1.11.20+ will populate this.</p>
                ) : (
                    <table className="min-w-full text-sm">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Death Kind</th>
                                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Sessions</th>
                                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Cast Attempts</th>
                                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Avg Session (s)</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {deathKinds.map(r => (
                                <tr key={r.death_kind}>
                                    <td className="px-3 py-2 font-mono text-xs">{r.death_kind || '—'}</td>
                                    <td className="px-3 py-2 text-right">{r.app_session_count}</td>
                                    <td className="px-3 py-2 text-right">{r.total_cast_attempts || 0}</td>
                                    <td className="px-3 py-2 text-right">{Math.round(r.avg_app_session_s || 0)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Cast outcomes by protocol × source (v1.11.20 §A.2) */}
            <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Cast Outcomes by Protocol × Source (last 30d)</h3>
                {castOutcomes.length === 0 ? (
                    <p className="text-sm text-gray-400">No session_terminated events yet — devices on v1.11.20+ will populate this.</p>
                ) : (
                    <table className="min-w-full text-sm">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Protocol</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Result</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">End Reason</th>
                                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Sessions</th>
                                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Avg Dur (s)</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {castOutcomes.slice(0, 50).map((r, i) => (
                                <tr key={i}>
                                    <td className="px-3 py-2 font-mono text-xs">{r.target_protocol || '—'}</td>
                                    <td className="px-3 py-2 font-mono text-xs">{r.content_origin || '—'}</td>
                                    <td className="px-3 py-2">
                                        <span className={`px-2 py-0.5 rounded text-xs ${
                                            r.result === 'ok' ? 'bg-green-100 text-green-800' :
                                            r.result === 'error' ? 'bg-red-100 text-red-800' :
                                            'bg-gray-100 text-gray-700'
                                        }`}>{r.result || '—'}</span>
                                    </td>
                                    <td className="px-3 py-2 font-mono text-xs">{r.end_reason || '—'}</td>
                                    <td className="px-3 py-2 text-right">{r.session_count}</td>
                                    <td className="px-3 py-2 text-right">{Math.round(r.avg_duration_s || 0)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            <EventDetailDrawer event={selectedEvent} onClose={() => setSelectedEvent(null)} />
        </div>
    );
}

// Drawer that slides in from the right with the full event payload + any
// contextual links (session detail, device profile, related dialog id).
function EventDetailDrawer({ event, onClose }) {
    const [timeline, setTimeline] = useState(null);
    const [timelineLoading, setTimelineLoading] = useState(false);

    const dialogId = event?.properties?.dialog_id || null;

    useEffect(() => {
        if (!dialogId) {
            setTimeline(null);
            return;
        }
        let cancelled = false;
        setTimelineLoading(true);
        eventsAPI.getDialogTimeline(dialogId)
            .then(r => { if (!cancelled) setTimeline(r.data); })
            .catch(() => { if (!cancelled) setTimeline(null); })
            .finally(() => { if (!cancelled) setTimelineLoading(false); });
        return () => { cancelled = true; };
    }, [dialogId]);

    if (!event) return null;
    const p = event.properties || {};
    const sessionId = p.session_id || null;
    const appSessionId = p.app_session_id || null;
    const deviceId = event.device_id || null;
    const ts = new Date(event.timestamp);

    return (
        <>
            <div
                className="fixed inset-0 bg-black/30 z-40"
                onClick={onClose}
            />
            <div className="fixed right-0 top-0 bottom-0 w-full max-w-xl bg-white shadow-2xl z-50 overflow-y-auto">
                <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
                    <div>
                        <div className="font-mono text-sm text-blue-700">{event.event_type}</div>
                        <div className="text-xs text-gray-500 mt-0.5">
                            {ts.toLocaleString()} · id #{event.id}
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-700 text-xl leading-none px-2">
                        ×
                    </button>
                </div>

                <div className="p-6 space-y-5">
                    {/* Quick context strip */}
                    <div className="grid grid-cols-2 gap-3 text-xs">
                        <KV label="Device ID" value={deviceId ? deviceId.substring(0, 12) + '…' : '—'} mono />
                        <KV label="App Session" value={appSessionId ? appSessionId.substring(0, 12) + '…' : '—'} mono />
                        <KV label="Session ID" value={sessionId ? sessionId.substring(0, 12) + '…' : '—'} mono />
                        <KV label="Dialog ID" value={dialogId ? dialogId.substring(0, 12) + '…' : '—'} mono />
                    </div>

                    {/* Drill-downs */}
                    <div className="flex flex-wrap gap-2">
                        {sessionId && (
                            <Link
                                to={`/admin/session/${sessionId}`}
                                className="px-3 py-1.5 rounded bg-blue-600 text-white text-xs font-medium hover:bg-blue-700">
                                Open session detail →
                            </Link>
                        )}
                        {appSessionId && (
                            <Link
                                to={`/admin/live-events?filter=app_session&app_session_id=${appSessionId}`}
                                className="px-3 py-1.5 rounded bg-gray-200 text-gray-800 text-xs font-medium hover:bg-gray-300">
                                Same app session
                            </Link>
                        )}
                        {dialogId && (
                            <span className="px-3 py-1.5 rounded bg-gray-100 text-gray-600 text-xs font-mono">
                                dialog: {dialogId.substring(0, 8)}
                            </span>
                        )}
                    </div>

                    {/* Pretty summary line for the well-known event types */}
                    <div className="bg-gray-50 rounded p-3 text-sm text-gray-700 leading-relaxed">
                        {summarise(event)}
                    </div>

                    {/* Connection-dialog step ladder (Miracast / CC / DLNA picker) */}
                    {dialogId && (
                        <DialogStepLadder
                            timeline={timeline}
                            loading={timelineLoading}
                        />
                    )}

                    {/* Full raw properties */}
                    <div>
                        <div className="text-xs font-medium uppercase tracking-wider text-gray-500 mb-2">
                            Raw properties
                        </div>
                        <pre className="bg-gray-900 text-green-200 text-xs rounded p-3 overflow-x-auto whitespace-pre-wrap break-all">
{JSON.stringify(p, null, 2)}
                        </pre>
                    </div>
                </div>
            </div>
        </>
    );
}

function KV({ label, value, mono }) {
    return (
        <div>
            <div className="uppercase tracking-wider text-gray-500 text-[10px]">{label}</div>
            <div className={`text-gray-900 mt-0.5 ${mono ? 'font-mono' : ''}`}>{value}</div>
        </div>
    );
}

// Renders a stepwise vertical ladder for one connection dialog. Green ✓ for
// completed steps, red ✕ for the step that failed/was cancelled, with the
// duration the step took. Drives "where did Miracast stall" for one session.
function DialogStepLadder({ timeline, loading }) {
    if (loading) {
        return (
            <div>
                <div className="text-xs font-medium uppercase tracking-wider text-gray-500 mb-2">
                    Connection step ladder
                </div>
                <div className="text-sm text-gray-400 italic">Loading dialog timeline…</div>
            </div>
        );
    }
    if (!timeline || !timeline.steps?.length) {
        return (
            <div>
                <div className="text-xs font-medium uppercase tracking-wider text-gray-500 mb-2">
                    Connection step ladder
                </div>
                <div className="text-sm text-gray-400 italic">No step events recorded for this dialog.</div>
            </div>
        );
    }

    const finalState = timeline.final?.state;
    const totalMs = timeline.final?.total_duration_ms;
    const stateClass =
        finalState === 'success'   ? 'bg-green-100 text-green-800' :
        finalState === 'cancelled' ? 'bg-yellow-100 text-yellow-800' :
        finalState === 'failed'    ? 'bg-red-100 text-red-800' :
                                     'bg-gray-100 text-gray-700';

    return (
        <div>
            <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-medium uppercase tracking-wider text-gray-500">
                    Connection step ladder
                </div>
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${stateClass}`}>
                    {finalState || 'in progress'}
                    {totalMs != null && <span className="ml-1 opacity-70">· {Math.round(totalMs / 100) / 10}s</span>}
                </span>
            </div>

            <div className="bg-gray-50 rounded p-3 space-y-1">
                {timeline.steps.map((s, i) => {
                    const isLast = i === timeline.steps.length - 1;
                    const sym  = s.status === 'success'   ? '✓' :
                                 s.status === 'cancelled' ? '⊘' :
                                                             '✕';
                    const cls  = s.status === 'success'   ? 'text-green-600' :
                                 s.status === 'cancelled' ? 'text-yellow-600' :
                                                             'text-red-600';
                    const dur  = s.duration_ms != null
                                    ? `${Math.round(s.duration_ms / 100) / 10}s`
                                    : '—';
                    const cum  = s.cumulative_ms != null
                                    ? `(t=${Math.round(s.cumulative_ms / 100) / 10}s)`
                                    : '';
                    return (
                        <div key={i} className="flex items-center gap-3 text-sm">
                            <span className={`font-mono font-bold w-5 text-center ${cls}`}>{sym}</span>
                            <span className="font-mono text-gray-800 flex-1">{s.step_name}</span>
                            <span className="font-mono text-xs text-gray-500">{dur}</span>
                            <span className="font-mono text-[10px] text-gray-400 w-16 text-right">{cum}</span>
                            {isLast && s.status !== 'success' && (
                                <span className="text-xs font-medium text-red-700">← stopped here</span>
                            )}
                        </div>
                    );
                })}
            </div>

            <div className="text-[11px] text-gray-500 mt-1 px-1">
                {timeline.device_name || '?'} · {timeline.event_count} event(s) · dialog {timeline.dialog_id?.substring(0, 8)}
            </div>
        </div>
    );
}

// ── helpers ──

function KpiCard({ title, value, subtitle }) {
    return (
        <div className="bg-white rounded-lg shadow-md p-4">
            <div className="text-xs uppercase tracking-wider text-gray-500">{title}</div>
            <div className="text-2xl font-bold text-gray-900 mt-1">{value}</div>
            <div className="text-xs text-gray-500 mt-1 truncate">{subtitle}</div>
        </div>
    );
}

function Chip({ active, onClick, children, badge, highlight }) {
    const base = 'px-3 py-1 rounded-full text-xs font-medium transition cursor-pointer ';
    const cls = active
        ? (highlight ? 'bg-blue-600 text-white' : 'bg-gray-700 text-white')
        : (highlight ? 'bg-blue-100 text-blue-800 hover:bg-blue-200' : 'bg-gray-100 text-gray-700 hover:bg-gray-200');
    return (
        <span className={base + cls} onClick={onClick}>
            {children}
            {badge !== undefined && badge !== null && (
                <span className="ml-2 px-1.5 py-0.5 rounded-full bg-white/30 text-xs">{badge}</span>
            )}
        </span>
    );
}

function summarise(ev) {
    const p = ev.properties || {};
    if (ev.event_type === 'app_session_terminated') {
        return `death=${p.death_kind || '?'} dur=${Math.round((p.duration_ms || 0) / 1000)}s casts=${p.cast_count_total || 0}`;
    }
    if (ev.event_type === 'app_session_started') {
        return `pid_start_kind=${p.start_kind || '?'} sdk=${p.sdk_version || '?'}`;
    }
    if (ev.event_type === 'session_terminated') {
        return `proto=${p.target_protocol || '?'} src=${p.content_origin || '?'} result=${p.result || '?'} reason=${p.end_reason || '?'} dur=${Math.round((p.duration_ms || 0) / 1000)}s`;
    }
    if (ev.event_type === 'play_integrity_unavailable') {
        return p.error_msg || p.exception || '';
    }
    return JSON.stringify(p).substring(0, 120);
}
